import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { 
  FiPlus, FiEdit, FiTrash2, FiSearch, FiCalendar, FiClock, FiUsers, 
  FiPhone, FiMail, FiMapPin, FiCheck, FiX, FiFilter, FiChevronLeft, 
  FiChevronRight, FiCheckCircle, FiXCircle, FiAlertCircle, FiGrid, 
  FiSettings, FiEye
} from 'react-icons/fi';

const DEFAULT_LOCATIONS = ['Indoor', 'Outdoor', 'VIP', 'Private Room', 'Bar Area'];
const LAYOUT_CHAIR_SIZE = 20;
const LAYOUT_CHAIR_GAP = 14;

const getChairAngleTowardsTable = (chairCenterX, chairCenterY, tableCenterX, tableCenterY) => {
  const angleToCenterDeg = Math.atan2(tableCenterY - chairCenterY, tableCenterX - chairCenterX) * (180 / Math.PI);
  return (angleToCenterDeg + 90) % 360;
};

const calculateLayoutChairPositions = (table) => {
  const chairCount = table.chair_count || table.capacity || 4;
  const width = table.width || 100;
  const height = table.height || 100;
  const shape = table.shape || 'rectangle';
  const rotation = (table.rotation || 0) * (Math.PI / 180);
  const centerX = (table.position_x || 0) + width / 2;
  const centerY = (table.position_y || 0) + height / 2;

  const positions = [];

  if (shape === 'circle' || shape === 'oval') {
    const radiusX = width / 2 + LAYOUT_CHAIR_GAP + (LAYOUT_CHAIR_SIZE / 2);
    const radiusY = height / 2 + LAYOUT_CHAIR_GAP + (LAYOUT_CHAIR_SIZE / 2);

    for (let i = 0; i < chairCount; i++) {
      const angleRad = (2 * Math.PI * i) / chairCount - (Math.PI / 2);
      const chairCenterX = centerX + radiusX * Math.cos(angleRad);
      const chairCenterY = centerY + radiusY * Math.sin(angleRad);
      const x = chairCenterX - (LAYOUT_CHAIR_SIZE / 2);
      const y = chairCenterY - (LAYOUT_CHAIR_SIZE / 2);
      const chairAngleDeg = getChairAngleTowardsTable(chairCenterX, chairCenterY, centerX, centerY);
      positions.push({ x, y, angle: chairAngleDeg });
    }
  } else {
    const chairsPerSide = Math.ceil(chairCount / 4);
    let chairIndex = 0;

    for (let i = 0; i < chairsPerSide && chairIndex < chairCount; i++) {
      const x = (table.position_x || 0) + (width / (chairsPerSide + 1)) * (i + 1) - LAYOUT_CHAIR_SIZE / 2;
      const y = (table.position_y || 0) - LAYOUT_CHAIR_GAP - LAYOUT_CHAIR_SIZE;
      positions.push({
        x,
        y,
        angle: getChairAngleTowardsTable(x + LAYOUT_CHAIR_SIZE / 2, y + LAYOUT_CHAIR_SIZE / 2, centerX, centerY)
      });
      chairIndex++;
    }

    for (let i = 0; i < chairsPerSide && chairIndex < chairCount; i++) {
      const x = (table.position_x || 0) + width + LAYOUT_CHAIR_GAP;
      const y = (table.position_y || 0) + (height / (chairsPerSide + 1)) * (i + 1) - LAYOUT_CHAIR_SIZE / 2;
      positions.push({
        x,
        y,
        angle: getChairAngleTowardsTable(x + LAYOUT_CHAIR_SIZE / 2, y + LAYOUT_CHAIR_SIZE / 2, centerX, centerY)
      });
      chairIndex++;
    }

    for (let i = 0; i < chairsPerSide && chairIndex < chairCount; i++) {
      const x = (table.position_x || 0) + (width / (chairsPerSide + 1)) * (i + 1) - LAYOUT_CHAIR_SIZE / 2;
      const y = (table.position_y || 0) + height + LAYOUT_CHAIR_GAP;
      positions.push({
        x,
        y,
        angle: getChairAngleTowardsTable(x + LAYOUT_CHAIR_SIZE / 2, y + LAYOUT_CHAIR_SIZE / 2, centerX, centerY)
      });
      chairIndex++;
    }

    for (let i = 0; i < chairsPerSide && chairIndex < chairCount; i++) {
      const x = (table.position_x || 0) - LAYOUT_CHAIR_GAP - LAYOUT_CHAIR_SIZE;
      const y = (table.position_y || 0) + (height / (chairsPerSide + 1)) * (i + 1) - LAYOUT_CHAIR_SIZE / 2;
      positions.push({
        x,
        y,
        angle: getChairAngleTowardsTable(x + LAYOUT_CHAIR_SIZE / 2, y + LAYOUT_CHAIR_SIZE / 2, centerX, centerY)
      });
      chairIndex++;
    }
  }

  return positions.map((pos) => {
    const chairCenterX = pos.x + LAYOUT_CHAIR_SIZE / 2;
    const chairCenterY = pos.y + LAYOUT_CHAIR_SIZE / 2;
    const dx = chairCenterX - centerX;
    const dy = chairCenterY - centerY;
    const rotatedCenterX = centerX + dx * Math.cos(rotation) - dy * Math.sin(rotation);
    const rotatedCenterY = centerY + dx * Math.sin(rotation) + dy * Math.cos(rotation);

    return {
      ...pos,
      x: rotatedCenterX - LAYOUT_CHAIR_SIZE / 2,
      y: rotatedCenterY - LAYOUT_CHAIR_SIZE / 2,
      angle: pos.angle + (table.rotation || 0)
    };
  });
};

function Reservations() {
  const { alert, confirm } = useModal();
  const [reservations, setReservations] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'calendar', or 'layout'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [tableLayout, setTableLayout] = useState(null);
  const [loadingLayout, setLoadingLayout] = useState(false);
  const [locationOptions, setLocationOptions] = useState(DEFAULT_LOCATIONS);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedTable, setSelectedTable] = useState(null);
  const [showSlotsModal, setShowSlotsModal] = useState(false);
  const [tableSlots, setTableSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settings, setSettings] = useState({});
  const [availableTables, setAvailableTables] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [panMode, setPanMode] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [layoutScale, setLayoutScale] = useState(1);
  const [isLayoutFullscreen, setIsLayoutFullscreen] = useState(false);
  const [layoutStatusTime, setLayoutStatusTime] = useState('');
  const [updatingReservationIds, setUpdatingReservationIds] = useState(new Set());
  const [cancellingReservationIds, setCancellingReservationIds] = useState(new Set());

  const normalizeTimeToHHMMSS = (timeValue) => {
    if (!timeValue) return '';
    const raw = String(timeValue).trim();
    const parts = raw.split(':');
    if (parts.length === 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:00`;
    }
    if (parts.length === 3) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${parts[2].padStart(2, '0')}`;
    }
    return '';
  };
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    table_id: '',
    reservation_date: new Date().toISOString().split('T')[0],
    reservation_time: '',
    party_size: '',
    special_requests: '',
    notes: ''
  });

  const fetchReservations = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedDate) params.append('date', selectedDate);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await api.get(`/api/reservations?${params.toString()}`);
      setReservations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch reservations:', error);
      await alert('Failed to fetch reservations', 'Error', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, statusFilter]);

  const fetchTables = useCallback(async () => {
    try {
      const response = await api.get('/api/tables');
      setTables(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tables:', error);
    }
  }, []);

  const fetchTableLayout = useCallback(async () => {
    try {
      setLoadingLayout(true);
      const response = await api.get(`/api/reservations/table-layout?date=${selectedDate}`);
      setTableLayout(response.data);
    } catch (error) {
      console.error('Failed to fetch table layout:', error);
      await alert('Failed to fetch table layout', 'Error', 'error');
    } finally {
      setLoadingLayout(false);
    }
  }, [selectedDate]);

  const fetchTableSlots = useCallback(async (tableId) => {
    try {
      setLoadingSlots(true);
      const response = await api.get(`/api/reservations/table/${tableId}/slots/${selectedDate}`);
      setTableSlots(response.data);
      setShowSlotsModal(true);
    } catch (error) {
      console.error('Failed to fetch table slots:', error);
      await alert('Failed to fetch table slots', 'Error', 'error');
    } finally {
      setLoadingSlots(false);
    }
  }, [selectedDate]);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/reservations/settings');
      const settingsObj = {};
      (response.data || []).forEach(setting => {
        settingsObj[setting.setting_key] = setting.setting_value;
      });
      setSettings(settingsObj);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get('/api/settings/restaurant');
      const rawLocations = response.data?.table_locations || '';
      const parsed = rawLocations
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

      setLocationOptions(parsed.length > 0 ? parsed : DEFAULT_LOCATIONS);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      setLocationOptions(DEFAULT_LOCATIONS);
    }
  };

  const updateSettings = async (updates) => {
    try {
      await api.put('/api/reservations/settings', updates);
      await fetchSettings();
      setShowSettingsModal(false);
      await alert('Settings updated successfully!', 'Success', 'success');
      if (viewMode === 'layout') {
        fetchTableLayout();
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
      await alert('Failed to update settings', 'Error', 'error');
    }
  };

  const fetchAvailableTables = useCallback(async () => {
    if (!formData.reservation_date || !formData.reservation_time || !formData.party_size) {
      setAvailableTables([]);
      return;
    }
    try {
      const response = await api.get(
        `/api/reservations/available-tables-slots?date=${formData.reservation_date}&time=${formData.reservation_time}&party_size=${formData.party_size}`
      );
      setAvailableTables(response.data.available_tables || []);
    } catch (error) {
      console.error('Failed to fetch available tables:', error);
      setAvailableTables([]);
    }
  }, [formData.reservation_date, formData.reservation_time, formData.party_size]);

  const fetchTimeSlots = useCallback(async () => {
    if (!formData.reservation_date || !formData.party_size) {
      setAvailableTimeSlots([]);
      return;
    }
    try {
      const response = await api.get(
        `/api/reservations/time-slots?date=${formData.reservation_date}&party_size=${formData.party_size}`
      );
      setAvailableTimeSlots(response.data.timeSlots || []);
    } catch (error) {
      console.error('Failed to fetch time slots:', error);
      setAvailableTimeSlots([]);
    }
  }, [formData.reservation_date, formData.party_size]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        party_size: parseInt(formData.party_size),
        table_id: formData.table_id || null
      };

      if (editingReservation) {
        await api.put(`/api/reservations/${editingReservation.id}`, submitData);
      } else {
        await api.post('/api/reservations', submitData);
      }
      
      fetchReservations();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save reservation:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save reservation';
      await alert(errorMessage, 'Error', 'error');
    }
  };

  const handleStatusChange = useCallback(async (id, newStatus) => {
    if (!id || !newStatus || updatingReservationIds.has(id)) {
      return;
    }

    const previousReservations = reservations;
    setUpdatingReservationIds((prev) => new Set(prev).add(id));
    setReservations((prev) => prev.map((reservation) => (
      reservation.id === id
        ? { ...reservation, status: newStatus }
        : reservation
    )));

    try {
      await api.put(`/api/reservations/${id}`, { status: newStatus });
      fetchReservations();
    } catch (error) {
      console.error('Failed to update status:', error);
      setReservations(previousReservations);
      await alert('Failed to update reservation status', 'Error', 'error');
    } finally {
      setUpdatingReservationIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [fetchReservations, alert, updatingReservationIds, reservations]);

  const handleCancel = useCallback(async (id) => {
    if (!id || cancellingReservationIds.has(id)) {
      return;
    }

    const confirmed = await confirm('Are you sure you want to cancel this reservation?', 'Cancel Reservation', 'warning');
    if (!confirmed) return;

    const previousReservations = reservations;
    setCancellingReservationIds((prev) => new Set(prev).add(id));
    setReservations((prev) => prev.map((reservation) => (
      reservation.id === id
        ? { ...reservation, status: 'cancelled' }
        : reservation
    )));
    
    try {
      await api.patch(`/api/reservations/${id}/cancel`, {});
      fetchReservations();
    } catch (error) {
      console.error('Failed to cancel reservation:', error);
      setReservations(previousReservations);
      await alert('Failed to cancel reservation', 'Error', 'error');
    } finally {
      setCancellingReservationIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, [fetchReservations, confirm, alert, cancellingReservationIds, reservations]);

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      table_id: '',
      reservation_date: new Date().toISOString().split('T')[0],
      reservation_time: '',
      party_size: '',
      special_requests: '',
      notes: ''
    });
    setEditingReservation(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'seated': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'no_show': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredReservations = useMemo(() => {
    if (!searchTerm) return reservations;
    const searchLower = searchTerm.toLowerCase();
    return reservations.filter(res => 
      res.customer_name?.toLowerCase().includes(searchLower) ||
      res.customer_phone?.includes(searchTerm) ||
      res.reservation_number?.toLowerCase().includes(searchLower)
    );
  }, [reservations, searchTerm]);

  const navigateDate = useCallback((days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  }, [selectedDate]);

  const formatDateDisplay = useCallback((dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return { weekday: '', date: '' };
      return {
        weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      };
    } catch (error) {
      return { weekday: '', date: '' };
    }
  }, []);

  const dateDisplay = formatDateDisplay(selectedDate);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (!selectedLocation && locationOptions.length > 0) {
      setSelectedLocation(locationOptions[0]);
    }
  }, [locationOptions, selectedLocation]);

  useEffect(() => {
    fetchReservations();
    fetchTables();
    if (viewMode === 'layout') {
      fetchTableLayout();
    }
  }, [selectedDate, statusFilter, viewMode, fetchReservations, fetchTables, fetchTableLayout]);

  // Debounce fetchAvailableTables
  useEffect(() => {
    if (formData.reservation_date && formData.reservation_time && formData.party_size) {
      const timeoutId = setTimeout(() => {
        fetchAvailableTables();
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setAvailableTables([]);
    }
  }, [formData.reservation_date, formData.reservation_time, formData.party_size, fetchAvailableTables]);

  // Auto-fetch time slots when date and party size change
  useEffect(() => {
    if (formData.reservation_date && formData.party_size) {
      const timeoutId = setTimeout(() => {
        fetchTimeSlots();
      }, 300);
      return () => clearTimeout(timeoutId);
    } else {
      setAvailableTimeSlots([]);
    }
  }, [formData.reservation_date, formData.party_size, fetchTimeSlots]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === 'Escape' || e.key === 'Esc') && isLayoutFullscreen) {
        setIsLayoutFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLayoutFullscreen]);

  const layoutTables = useMemo(() => {
    const tablesForDate = tableLayout?.tables || [];
    if (!selectedLocation) return tablesForDate;
    return tablesForDate.filter(table => (table.location || DEFAULT_LOCATIONS[0]) === selectedLocation);
  }, [tableLayout, selectedLocation]);

  const layoutContainerHeight = isLayoutFullscreen ? 'calc(100vh - 140px)' : 'calc(100vh - 350px)';

  const handleLayoutMouseDown = (e) => {
    if (!panMode) return;
    setIsPanning(true);
    setPanStart({
      x: e.clientX,
      y: e.clientY,
      offsetX: panOffset.x,
      offsetY: panOffset.y
    });
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isPanning) return;
      const deltaX = e.clientX - panStart.x;
      const deltaY = e.clientY - panStart.y;
      setPanOffset({
        x: panStart.offsetX + deltaX,
        y: panStart.offsetY + deltaY
      });
    };

    const handleMouseUp = () => {
      if (isPanning) {
        setIsPanning(false);
      }
    };

    if (isPanning) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isPanning, panStart]);

  if (loading) {
    return <Layout title="Reservations"><div>Loading...</div></Layout>;
  }

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Reservations</h1>
        <p className="text-sm text-gray-600">Manage table reservations and bookings</p>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by name, phone, or reservation number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
            />
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-md transition-all text-sm ${
                  viewMode === 'list' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                List
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-3 py-1.5 rounded-md transition-all text-sm ${
                  viewMode === 'calendar' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Calendar
              </button>
              <button
                onClick={() => {
                  setViewMode('layout');
                  fetchTableLayout();
                }}
                className={`px-3 py-1.5 rounded-md transition-all text-sm flex items-center gap-1 ${
                  viewMode === 'layout' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FiGrid className="h-3 w-3" /> Layout
              </button>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm bg-white"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="seated">Seated</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <button
              onClick={() => {
                fetchSettings();
                setShowSettingsModal(true);
              }}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm transition-colors"
            >
              <FiSettings className="h-4 w-4" />
            </button>

            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm transition-colors"
            >
              <FiPlus className="h-4 w-4" /> New
            </button>
          </div>
        </div>
      </div>

      {/* Date Navigation - Separate Container */}
      <div className="flex justify-end mb-6">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 inline-flex items-center gap-3">
          {/* Previous Day Button */}
          <button
            onClick={() => navigateDate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 hover:border-gray-300 flex-shrink-0"
            title="Previous Day"
          >
            <FiChevronLeft className="h-4 w-4 text-gray-700" />
          </button>

          {/* Date Display & Picker */}
          <div className="relative flex items-center gap-3 px-4 py-2.5 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-all cursor-pointer group hover:shadow-sm">
            <FiCalendar className="h-4 w-4 text-gray-600 group-hover:text-gray-900 transition-colors flex-shrink-0" />
            <div className="flex flex-col items-start">
              <span className="text-xs text-gray-500 leading-tight font-medium">
                {dateDisplay.weekday}
              </span>
              <span className="text-sm font-bold text-gray-900 leading-tight">
                {dateDisplay.date}
              </span>
            </div>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="absolute inset-0 opacity-0 cursor-pointer"
            />
          </div>

          {/* Next Day Button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              navigateDate(1);
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200 hover:border-gray-300 flex-shrink-0"
            title="Next Day"
          >
            <FiChevronRight className="h-4 w-4 text-gray-700" />
          </button>

          {/* Today Button */}
          <button
            onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
            className="px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 text-sm font-medium transition-colors whitespace-nowrap"
          >
            Today
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg border-0 shadow-sm p-4">
          <div className="text-xs text-gray-600 mb-1">Total</div>
          <div className="text-xl font-bold text-gray-900">{reservations.length}</div>
        </div>
        <div className="bg-white rounded-lg border-0 shadow-sm p-4">
          <div className="text-xs text-gray-600 mb-1">Pending</div>
          <div className="text-xl font-bold text-yellow-600">
            {reservations.filter(r => r.status === 'pending').length}
          </div>
        </div>
        <div className="bg-white rounded-lg border-0 shadow-sm p-4">
          <div className="text-xs text-gray-600 mb-1">Confirmed</div>
          <div className="text-xl font-bold text-green-600">
            {reservations.filter(r => r.status === 'confirmed').length}
          </div>
        </div>
        <div className="bg-white rounded-lg border-0 shadow-sm p-4">
          <div className="text-xs text-gray-600 mb-1">Seated</div>
          <div className="text-xl font-bold text-blue-600">
            {reservations.filter(r => r.status === 'seated').length}
          </div>
        </div>
        <div className="bg-white rounded-lg border-0 shadow-sm p-4">
          <div className="text-xs text-gray-600 mb-1">Completed</div>
          <div className="text-xl font-bold text-gray-600">
            {reservations.filter(r => r.status === 'completed').length}
          </div>
        </div>
      </div>

      {/* Reservations List */}
      {viewMode !== 'layout' && (
        <>
          {filteredReservations.length === 0 ? (
            <div className="bg-white rounded-lg border-0 shadow-sm p-12 text-center">
              <FiCalendar className="text-4xl text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No reservations found</p>
              <p className="text-gray-400 text-sm mt-2">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'Create your first reservation to get started'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredReservations.map((reservation) => (
                <div
                  key={reservation.id}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 hover:shadow-lg hover:scale-[1.01] transition-all duration-200"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-lg font-bold text-gray-900">{reservation.customer_name}</h3>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(reservation.status)}`}>
                          {reservation.status.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500">#{reservation.reservation_number}</span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs text-gray-600">
                        <div className="flex items-center gap-2">
                          <FiCalendar className="text-gray-400 h-3 w-3" />
                          <span>{new Date(reservation.reservation_date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FiClock className="text-gray-400 h-3 w-3" />
                          <span>{reservation.reservation_time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FiUsers className="text-gray-400 h-3 w-3" />
                          <span>{reservation.party_size} guests</span>
                        </div>
                        {reservation.tables && (
                          <div className="flex items-center gap-2">
                            <FiMapPin className="text-gray-400 h-3 w-3" />
                            <span>Table {reservation.tables.table_number}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <FiPhone className="text-gray-400 h-3 w-3" />
                          <span>{reservation.customer_phone}</span>
                        </div>
                        {reservation.customer_email && (
                          <div className="flex items-center gap-2">
                            <FiMail className="text-gray-400 h-3 w-3" />
                            <span className="truncate">{reservation.customer_email}</span>
                          </div>
                        )}
                      </div>

                      {reservation.special_requests && (
                        <div className="mt-3 p-2.5 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-700">
                            <strong>Special Requests:</strong> {reservation.special_requests}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-row md:flex-col gap-2">
                      {reservation.status === 'pending' && (
                        <button
                          onClick={() => handleStatusChange(reservation.id, 'confirmed')}
                          disabled={updatingReservationIds.has(reservation.id)}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 text-xs transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <FiCheckCircle className="h-3 w-3" /> {updatingReservationIds.has(reservation.id) ? 'Updating...' : 'Confirm'}
                        </button>
                      )}
                      {reservation.status === 'confirmed' && (
                        <button
                          onClick={() => handleStatusChange(reservation.id, 'seated')}
                          disabled={updatingReservationIds.has(reservation.id)}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-xs transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <FiCheckCircle className="h-3 w-3" /> {updatingReservationIds.has(reservation.id) ? 'Updating...' : 'Mark Seated'}
                        </button>
                      )}
                      {reservation.status === 'seated' && (
                        <button
                          onClick={() => handleStatusChange(reservation.id, 'completed')}
                          disabled={updatingReservationIds.has(reservation.id)}
                          className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center justify-center gap-2 text-xs transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <FiCheckCircle className="h-3 w-3" /> {updatingReservationIds.has(reservation.id) ? 'Updating...' : 'Complete'}
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditingReservation(reservation);
                          setFormData({
                            customer_name: reservation.customer_name,
                            customer_phone: reservation.customer_phone,
                            customer_email: reservation.customer_email || '',
                            table_id: reservation.table_id || '',
                            reservation_date: reservation.reservation_date,
                            reservation_time: reservation.reservation_time,
                            party_size: reservation.party_size.toString(),
                            special_requests: reservation.special_requests || '',
                            notes: reservation.notes || ''
                          });
                          setShowModal(true);
                        }}
                        className="px-2.5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 text-xs transition-colors"
                        title="Edit"
                      >
                        <FiEdit className="h-3 w-3" />
                      </button>
                      {reservation.status !== 'cancelled' && reservation.status !== 'completed' && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCancel(reservation.id);
                          }}
                          disabled={cancellingReservationIds.has(reservation.id)}
                          className="px-2.5 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center justify-center gap-2 text-xs transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                          title="Cancel"
                        >
                          <FiXCircle className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4" onClick={() => {
          setShowModal(false);
          resetForm();
        }}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingReservation ? 'Edit Reservation' : 'New Reservation'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Customer Name *</label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Party Size *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.party_size}
                    onChange={(e) => setFormData({ ...formData, party_size: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    value={formData.reservation_date}
                    onChange={(e) => setFormData({ ...formData, reservation_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Time *</label>
                  <div className="space-y-2">
                    <input
                      type="time"
                      value={formData.reservation_time}
                      onChange={(e) => setFormData({ ...formData, reservation_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      required
                    />
                    {formData.reservation_date && formData.party_size && (
                      <button
                        type="button"
                        onClick={fetchTimeSlots}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Show Available Time Slots
                      </button>
                    )}
                    {availableTimeSlots.length > 0 && (
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        {availableTimeSlots.slice(0, 12).map((slot, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setFormData({ ...formData, reservation_time: slot })}
                            className={`px-2 py-1 text-xs rounded border transition-colors ${
                              formData.reservation_time === slot
                                ? 'bg-gray-900 text-white border-gray-900'
                                : 'bg-white border-gray-300 hover:border-gray-900'
                            }`}
                          >
                            {slot.substring(0, 5)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Table {formData.reservation_time && formData.party_size ? '(Available Tables)' : '(Optional)'}
                  </label>
                  {formData.reservation_time && formData.party_size && availableTables.length > 0 ? (
                    <div className="space-y-2">
                      <select
                        value={formData.table_id}
                        onChange={(e) => setFormData({ ...formData, table_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      >
                        <option value="">Select an available table</option>
                        {availableTables.map(table => (
                          <option key={table.id} value={table.id}>
                            Table {table.table_number} - Capacity: {table.capacity} ({table.location || 'Main Floor'})
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-green-600">
                        ✓ {availableTables.length} table(s) available at {formData.reservation_time}
                      </p>
                    </div>
                  ) : formData.reservation_time && formData.party_size && availableTables.length === 0 ? (
                    <div>
                      <select
                        value={formData.table_id}
                        onChange={(e) => setFormData({ ...formData, table_id: e.target.value })}
                        className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      >
                        <option value="">No tables available - Select manually</option>
                        {tables.map(table => (
                          <option key={table.id} value={table.id}>
                            Table {table.table_number} - Capacity: {table.capacity} ({table.location})
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-red-600 mt-1">
                        ⚠ No tables available at this time. You can still select a table manually.
                      </p>
                    </div>
                  ) : (
                    <select
                      value={formData.table_id}
                      onChange={(e) => setFormData({ ...formData, table_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    >
                      <option value="">Select a table (enter date, time & party size to see available tables)</option>
                      {tables.map(table => (
                        <option key={table.id} value={table.id}>
                          Table {table.table_number} - Capacity: {table.capacity} ({table.location})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Special Requests</label>
                  <textarea
                    value={formData.special_requests}
                    onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    rows="2"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Internal Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    rows="2"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800 transition-colors text-sm"
                >
                  {editingReservation ? 'Update' : 'Create'} Reservation
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table Layout View */}
      {viewMode === 'layout' && (
        <div className={isLayoutFullscreen ? 'fixed inset-0 z-50 bg-gray-50 p-4 overflow-hidden' : ''}>
          <div className="bg-white rounded-lg border-0 shadow-sm p-4 mb-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Table Layout - {new Date(selectedDate).toLocaleDateString()}</h2>
                <p className="text-xs text-gray-600 mt-1">View reservations by clicking on tables</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs font-semibold text-gray-600">Location</span>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="px-3 py-2 text-xs border border-gray-200 rounded-lg bg-white"
                >
                  {locationOptions.map((location) => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
                
                {/* Enhanced Time Filter */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <FiClock className="text-gray-600" size={16} />
                    <span className="text-sm font-semibold text-gray-700">Filter by Time</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative group">
                      <input
                        type="time"
                        value={layoutStatusTime}
                        onChange={(e) => setLayoutStatusTime(e.target.value)}
                        className="pl-4 pr-12 py-3 text-lg font-semibold border-2 border-gray-300 rounded-xl bg-white hover:border-gray-400 focus:border-black focus:ring-2 focus:ring-gray-200 transition-all shadow-sm min-w-[200px] cursor-pointer"
                        placeholder="Select time"
                        title="Click the clock icon on the right to select a time"
                      />
                      <FiClock className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover:text-gray-600 pointer-events-none animate-pulse" size={24} />
                      <span className="absolute -bottom-6 left-0 text-xs text-gray-500 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                        👆 Click the clock icon →
                      </span>
                    </div>
                    {layoutStatusTime && (
                      <button
                        onClick={() => setLayoutStatusTime('')}
                        className="px-4 py-3 text-sm font-semibold rounded-xl bg-red-100 text-red-700 hover:bg-red-200 transition-all flex items-center gap-2"
                      >
                        <FiX size={16} /> Clear
                      </button>
                    )}
                  </div>
                  {layoutStatusTime && (
                    <div className="px-3 py-2 rounded-lg bg-green-50 text-green-800 border border-green-300 font-semibold text-sm flex items-center gap-2">
                      <FiCheckCircle size={16} />
                      Showing availability at {layoutStatusTime}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs flex-wrap">
                <button
                  onClick={() => setPanMode(!panMode)}
                  className={`px-3 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                    panMode
                      ? 'bg-gray-900 text-white hover:bg-gray-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Pan
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600">Zoom</span>
                  <button
                    onClick={() => setLayoutScale(Math.max(0.5, layoutScale - 0.1))}
                    className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                  >
                    -
                  </button>
                  <span className="text-xs font-semibold w-10 text-center">{Math.round(layoutScale * 100)}%</span>
                  <button
                    onClick={() => setLayoutScale(Math.min(2, layoutScale + 0.1))}
                    className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={() => setIsLayoutFullscreen(!isLayoutFullscreen)}
                  className="px-3 py-2 rounded-lg font-semibold bg-gray-900 text-white hover:bg-gray-800"
                >
                  {isLayoutFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                </button>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 border-2 border-green-600 rounded"></div>
                  <span className="text-gray-600">Available</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-orange-500 border-2 border-orange-600 rounded"></div>
                  <span className="text-gray-600">Reserved</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 border-2 border-red-600 rounded"></div>
                  <span className="text-gray-600">Occupied</span>
                </div>
              </div>
            </div>
          </div>
          
          {loadingLayout ? (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto mb-3"></div>
              <p className="text-gray-500">Loading table layout...</p>
            </div>
          ) : tableLayout && layoutTables.length > 0 ? (
            <div className="relative w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-gray-300 overflow-hidden">
              <div className="relative w-full overflow-auto" style={{ 
                height: layoutContainerHeight,
                minHeight: '600px'
              }}>
                {/* Grid Background */}
                <div 
                  className="absolute inset-0 opacity-20 pointer-events-none"
                  style={{
                    backgroundImage: `
                      linear-gradient(to right, #cbd5e1 1px, transparent 1px),
                      linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)
                    `,
                    backgroundSize: '50px 50px'
                  }}
                />
                
                <div
                  className={panMode ? (isPanning ? 'cursor-grabbing' : 'cursor-grab') : ''}
                  onMouseDown={handleLayoutMouseDown}
                  style={{ 
                  position: 'relative',
                  width: '1200px',
                  height: '800px',
                  minWidth: '100%',
                  minHeight: '100%',
                  transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${layoutScale})`,
                  transformOrigin: 'top left'
                }}>
                  {layoutTables.map((table) => {
                    const selectedTimeHHMMSS = normalizeTimeToHHMMSS(layoutStatusTime);
                    const now = new Date();
                    const currentTimeHHMMSS = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:00`;
                    const effectiveTime = selectedTimeHHMMSS || currentTimeHHMMSS;

                    const isReservedAtEffectiveTime = (table.reservations || []).some((res) => {
                      const start = normalizeTimeToHHMMSS(res.time || res.reservation_time);
                      const end = normalizeTimeToHHMMSS(res.end_time);
                      if (!start || !end) return false;
                      return effectiveTime >= start && effectiveTime < end;
                    });

                    // With explicit time filter: show reservation availability for that slot only
                    // Without filter: keep existing behavior (occupied from POS/current state)
                    const isOccupied = selectedTimeHHMMSS
                      ? false
                      : Boolean(table.is_occupied);

                    const statusColor = isOccupied
                      ? '#ef4444'
                      : isReservedAtEffectiveTime
                      ? '#f97316'
                      : '#22c55e';
                    const shape = table.shape || 'rectangle';
                    const chairPositions = calculateLayoutChairPositions(table);
                    
                    return (
                      <React.Fragment key={table.id}>
                        {/* Chairs */}
                        {chairPositions.map((chair, idx) => (
                          <div
                            key={`chair-${table.id}-${idx}`}
                            className="absolute pointer-events-none"
                            style={{
                              left: `${chair.x}px`,
                              top: `${chair.y}px`,
                              width: `${LAYOUT_CHAIR_SIZE}px`,
                              height: `${LAYOUT_CHAIR_SIZE}px`,
                              transform: `rotate(${chair.angle}deg)`,
                              transformOrigin: 'center center',
                              opacity: 0.95
                            }}
                          >
                            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                              <div style={{
                                position: 'absolute',
                                bottom: '2px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '14px',
                                height: '11px',
                                background: 'linear-gradient(180deg, #6b7280 0%, #4b5563 100%)',
                                border: '1px solid #374151',
                                borderRadius: '3px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.35), inset 0 0.5px 1px rgba(255,255,255,0.15)'
                              }} />
                              <div style={{
                                position: 'absolute',
                                top: '1px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '12px',
                                height: '7px',
                                background: 'linear-gradient(180deg, #6b7280 0%, #4b5563 100%)',
                                border: '1px solid #374151',
                                borderRadius: '3px 3px 2px 2px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.3), inset 0 0.5px 1px rgba(255,255,255,0.12)'
                              }} />
                            </div>
                          </div>
                        ))}
                        
                        {/* Table */}
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchTableSlots(table.id);
                          }}
                          className="absolute cursor-pointer hover:scale-105 transition-all duration-200"
                          style={{
                            left: `${table.position_x || 0}px`,
                            top: `${table.position_y || 0}px`,
                            width: `${table.width || 100}px`,
                            height: `${table.height || 100}px`,
                            transform: `rotate(${table.rotation || 0}deg)`,
                            transformOrigin: 'center center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title={`Table ${table.table_number} - Click to view slots`}
                        >
                          <div style={{
                            position: 'absolute',
                            inset: '0',
                            borderRadius: (shape === 'circle' || shape === 'oval') ? '50%' : '8px',
                            backgroundColor: '#6b7280',
                            border: `4px solid ${statusColor}`,
                            boxShadow: `inset 0 2px 4px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.2)`
                          }} />
                          
                          <div className="text-center p-2 relative z-10" style={{ pointerEvents: 'none' }}>
                            <div className="font-bold text-sm text-white drop-shadow-lg" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                              {table.table_number}
                            </div>
                            <div className="text-xs text-white opacity-90 drop-shadow" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                              {table.capacity} seats
                            </div>
                            {table.reservations_count > 0 && (
                              <div className="text-xs font-semibold mt-1 text-white drop-shadow" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                {table.reservations_count} {table.reservations_count === 1 ? 'rsv' : 'rsvs'}
                              </div>
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
              <FiGrid className="text-4xl text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No tables found</p>
              <p className="text-gray-400 text-sm mt-2">Please set up your table layout in the Tables tab first.</p>
            </div>
          )}
        </div>
      )}

      {/* Table Slots Modal */}
      {showSlotsModal && tableSlots && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[75] p-4" onClick={() => {
          setShowSlotsModal(false);
          setTableSlots(null);
        }}>
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Table {tableSlots.table?.table_number} - Slots
                </h2>
                <p className="text-xs text-gray-600 mt-1">
                  {new Date(tableSlots.date).toLocaleDateString()} • Capacity: {tableSlots.table?.capacity}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowSlotsModal(false);
                  setTableSlots(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>

            <div className="mb-4 flex items-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-200 border border-green-400 rounded"></div>
                <span className="text-gray-600">Available ({tableSlots.available_slots})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-200 border border-red-400 rounded"></div>
                <span className="text-gray-600">Occupied ({tableSlots.occupied_slots})</span>
              </div>
            </div>

            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
              {tableSlots.slots.map((slot, idx) => (
                <div
                  key={idx}
                  className={`p-2.5 rounded-lg border-2 text-center transition-colors ${
                    slot.available
                      ? 'bg-green-50 border-green-300 hover:bg-green-100 cursor-pointer'
                      : 'bg-red-50 border-red-300'
                  }`}
                  onClick={() => {
                    if (slot.available) {
                      setFormData({
                        ...formData,
                        table_id: tableSlots.table.id,
                        reservation_date: tableSlots.date,
                        reservation_time: slot.time
                      });
                      setShowSlotsModal(false);
                      setShowModal(true);
                    }
                  }}
                >
                  <div className="text-xs font-semibold">{slot.time.substring(0, 5)}</div>
                  {slot.reservation && (
                    <div className="text-[10px] text-gray-600 mt-1 truncate">
                      {slot.reservation.customer_name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowSettingsModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Reservation Settings</h2>
              <button
                onClick={() => setShowSettingsModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Average Dining Time (minutes)
                </label>
                <input
                  type="number"
                  min="15"
                  max="240"
                  step="15"
                  value={settings.average_dining_time || '60'}
                  onChange={(e) => setSettings({ ...settings, average_dining_time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  placeholder="60"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tables will be automatically occupied for this duration when a reservation is made
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Time Slot Interval (minutes)
                </label>
                <select
                  value={settings.time_slot_interval || '30'}
                  onChange={(e) => setSettings({ ...settings, time_slot_interval: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="90">90 minutes</option>
                  <option value="120">120 minutes</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Opening Time
                  </label>
                  <input
                    type="time"
                    value={settings.opening_time?.substring(0, 5) || '11:00'}
                    onChange={(e) => setSettings({ ...settings, opening_time: e.target.value + ':00' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Closing Time
                  </label>
                  <input
                    type="time"
                    value={settings.closing_time?.substring(0, 5) || '23:00'}
                    onChange={(e) => setSettings({ ...settings, closing_time: e.target.value + ':00' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    const updates = {
                      average_dining_time: settings.average_dining_time || '60',
                      time_slot_interval: settings.time_slot_interval || '30',
                      opening_time: settings.opening_time || '11:00:00',
                      closing_time: settings.closing_time || '23:00:00'
                    };
                    updateSettings(updates);
                  }}
                  className="flex-1 bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800 transition-colors text-sm"
                >
                  Save Settings
                </button>
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Reservations;

